import {sweetAPI} from 'app/store/sweet-api';
import {providesTagsByURL, showMessages} from 'app/store/sweet-api/utils';
import {PRODUCTBUNDLE_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {MARKET_RTK_TAGS} from 'app/utils/rtk-tags';

import type {ToastMessageOptionsType} from 'app/toasts/slice';
import type {ListResponseType, ProductSerializedType} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [MARKET_RTK_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      products: build.query<
        ListResponseType<ProductSerializedType>,
        {
          team: string;
        }
      >({
        query: (params) => ({
          url: PRODUCTBUNDLE_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(MARKET_RTK_TAGS.TAGS),
      }),
      createProduct: build.mutation<
        ProductSerializedType,
        {team: string; html_url: string}
      >({
        query: (body) => ({
          url: PRODUCTBUNDLE_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) =>
          showMessages({
            onSuccess: ({html_url}) => {
              const segments = html_url.split('/');
              const base = segments.slice(0, -2);
              const url = `${base.join('/')}/products/?ordering=-created_at`;
              return getCreatedProductNotification(url);
            },
            onError: () => gt('Unable to create product.'),
          })(args, api),
        invalidatesTags: () => [{type: MARKET_RTK_TAGS.TAGS, id: 'list'}],
      }),
      updateProduct: build.mutation<
        ProductSerializedType,
        Pick<ProductSerializedType, 'url'> & Partial<ProductSerializedType>
      >({
        query: ({url, name, description, is_public}) => ({
          url,
          method: 'PATCH',
          body: {name, description, is_public},
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Product updated'),
          onError: () => gt('Unable to update product'),
        }),
        invalidatesTags: (product) => [
          {type: MARKET_RTK_TAGS.TAGS, id: 'list'},
          {type: MARKET_RTK_TAGS.TAGS, id: product?.url ?? ''},
        ],
      }),
      deleteProduct: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Product deleted'),
          onError: () => gt('Unable to delete product'),
        }),
        invalidatesTags: () => [{type: MARKET_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useCreateProductMutation: useCreateProduct,
  useProductsQuery: useProducts,
  useUpdateProductMutation: useUpdateProduct,
  useDeleteProductMutation: useDeleteProduct,
} = extendedAPI;

const getCreatedProductNotification = (
  productUrl: string
): [string, ToastMessageOptionsType] => {
  return [
    gt('Product created.'),
    {
      link: {
        text: gt('View product'),
        to: productUrl,
      },
      timeout: 8000,
    },
  ];
};
